// Builds the drawing as data (rects, lines, texts, paths in paint order) from the series and the SVG size.
// The component renders the scene; nothing here touches the DOM. Coordinates are formatted as chart.js did
// (one decimal for computed positions, integers for frame edges).
import type { ChartPayload } from "../api/types";
import { fmtClock, fmtDay, fmtDayShort, fmtEt, fmtPct, fmtPrice, wdEt } from "./format";
import { buildView, DAY_LABEL_PX, dayLabelPlan, niceTicks, pathOf, type Point } from "./layout";
import { liveOracleFor, oracleAt, oracleSteps, windowsIn } from "./series";
import type { ObservedFrozenWindow, SymbolSeries, View } from "./types";

export type SceneItem =
  | { kind: "rect"; cls: string; x: string; y: number; width: string; height: number }
  | { kind: "line"; cls: string; x1: number | string; y1: number | string; x2: number | string; y2: number | string }
  | { kind: "text"; cls: string; x: number | string; y: number | string; text: string; anchor?: "start" | "end" }
  | { kind: "path"; cls: string; d: string };

/** Path data of the clipped layers, in paint order. */
export interface SceneLayers {
  gapFill: string;
  gapLine: string;
  oracle: string;
  hourly: string;
  monitor: string;
  connector: string;
  live: string;
}

export interface LastMark {
  cx: string;
  cy: string;
  labelX: string;
  labelY: string;
  anchor: "start" | "end";
  text: string;
}

export interface Scene {
  view: View;
  /** Shading, grid, labels, axis and markers, in paint order (drawn under the clipped layers). */
  decor: SceneItem[];
  layers: SceneLayers;
  lastMark: LastMark | null;
}

export function buildScene(payload: ChartPayload, sym: string, s: SymbolSeries, W: number, H: number): Scene | null {
  const rows = s.all.filter((r) => r.pool != null || r.oracle != null);
  const steps = oracleSteps(s.entry);
  const view = buildView(rows, s.rec, W, H, steps, sym);
  if (!view) return null;
  const { padL, padR, mainTop, mainBot, gapTop, gapBot, t0, t1, x, y, yg, lo, hi, blo, bhi } = view;
  const shade: SceneItem[] = [], shadeLabels: SceneItem[] = [], grid: SceneItem[] = [], axis: SceneItem[] = [], ylab: SceneItem[] = [], marks: SceneItem[] = [];

  // shading: frozen windows and cash sessions
  for (const w of windowsIn(payload.sessions.cash, t0, t1)) {
    const a = x(Math.max(w.a, t0)), b = x(Math.min(w.b, t1));
    shade.push({ kind: "rect", cls: "cash", x: a.toFixed(1), y: mainTop, width: (b - a).toFixed(1), height: gapBot - mainTop });
    if (b - a > 60) shadeLabels.push({ kind: "text", cls: "label dim", x: (a + 6).toFixed(1), y: mainTop + 14, text: "NYSE cash" });
  }
  const frozen = windowsIn<ObservedFrozenWindow>(payload.sessions.frozen, t0, t1);
  for (const w of frozen) {
    const a = x(Math.max(w.a, t0)), b = x(Math.min(w.b, t1));
    shade.push({ kind: "rect", cls: "frozen", x: a.toFixed(1), y: mainTop, width: (b - a).toFixed(1), height: gapBot - mainTop });
    shade.push({ kind: "rect", cls: "frozen-hatch", x: a.toFixed(1), y: mainTop, width: (b - a).toFixed(1), height: gapBot - mainTop });
    const txt = b - a > 260 ? `feed frozen: ${wdEt(w.a)} 20:00 ET to ${wdEt(w.b)} 20:00 ET (${w.reason})` : b - a > 90 ? "feed frozen" : "";
    if (txt) shadeLabels.push({ kind: "text", cls: "label strong", x: (a + 6).toFixed(1), y: mainTop + 14, text: txt });
  }

  // grid and time axis: 00:00 UTC lines with day labels, 12:00 minor ticks when there is room
  const dayPx = (W - padL - padR) / ((t1 - t0) / 86400000);
  const days = dayLabelPlan(dayPx);
  const labelPx = days.short ? DAY_LABEL_PX.short : DAY_LABEL_PX.long;
  for (let t = Math.ceil(t0 / 3600000) * 3600000; t <= t1; t += 3600000) {
    const h = new Date(t).getUTCHours();
    const xt = x(t).toFixed(1);
    if (h === 0) {
      grid.push({ kind: "line", cls: "grid", x1: xt, y1: mainTop, x2: xt, y2: gapBot });
      const labelled = Math.floor(t / 86400000) % days.every === 0 && x(t) + labelPx - 12 < W - padR;
      if (labelled) axis.push({ kind: "text", cls: "label", x: (x(t) + 4).toFixed(1), y: H - 8, text: days.short ? fmtDayShort(t) : fmtDay(t) });
    } else if (h % 6 === 0 && dayPx > 300) {
      grid.push({ kind: "line", cls: "grid minor", x1: xt, y1: gapBot - 4, x2: xt, y2: gapBot });
      if (h === 12 && dayPx > 420) axis.push({ kind: "text", cls: "label dim", x: (x(t) + 4).toFixed(1), y: H - 8, text: "12:00" });
    }
  }
  grid.push({ kind: "line", cls: "grid", x1: padL, y1: mainBot, x2: W - padR, y2: mainBot });
  grid.push({ kind: "line", cls: "grid", x1: padL, y1: gapBot, x2: W - padR, y2: gapBot });
  grid.push({ kind: "line", cls: "zero", x1: padL, y1: yg(0).toFixed(1), x2: W - padR, y2: yg(0).toFixed(1) });

  // y labels at the right edge
  const feedNow = liveOracleFor(s) ?? steps.at(-1)?.price ?? null;
  for (const v of niceTicks(lo, hi, 4)) {
    if (feedNow == null || Math.abs(y(v) - y(feedNow)) > 14) ylab.push({ kind: "text", cls: "label", x: W - padR + 6, y: (y(v) + 4).toFixed(1), text: fmtPrice(v) });
  }
  if (feedNow != null) ylab.push({ kind: "text", cls: "label strong", x: W - padR + 6, y: (y(feedNow) + 4).toFixed(1), text: `feed ${fmtPrice(feedNow)}` });
  for (const v of niceTicks(blo, bhi, 3)) ylab.push({ kind: "text", cls: "label", x: W - padR + 6, y: (yg(v) + 4).toFixed(1), text: fmtPct(v) });
  ylab.push({ kind: "text", cls: "label dim", x: padL + 4, y: gapTop - 6, text: "gap over the feed" });

  // oracle: stepped at the on-chain updatedAt
  const opts: Point[] = [];
  const first = oracleAt(steps, t0);
  if (first) opts.push([x(t0), y(first.price)]);
  for (const st of steps) {
    if (st.at < t0 || st.at > t1) continue;
    const prev = opts.at(-1)?.[1] ?? y(st.price);
    opts.push([x(st.at), prev], [x(st.at), y(st.price)]);
  }
  const liveLast = s.ext.at(-1);
  const lastStepAt = steps.at(-1)?.at ?? 0;
  if (liveLast?.oracle != null && liveLast.updatedAt != null && liveLast.updatedAt > lastStepAt) {
    const prev = opts.at(-1)?.[1] ?? y(liveLast.oracle);
    const xa = x(Math.max(t0, liveLast.updatedAt));
    opts.push([xa, prev], [xa, y(liveLast.oracle)]);
  }
  const tail = opts.at(-1);
  if (tail) opts.push([x(t1), tail[1]]);

  // pool: hourly prefix (dashed), monitor readings, live extension
  const hourlyPts: Point[] = [];
  for (const r of s.rec) if (r.src === "hourly" && r.pool != null) hourlyPts.push([x(r.t), y(r.pool)]);
  const monRows = s.rec.filter((r) => r.src === "monitor");
  const firstMon = monRows[0];
  if (hourlyPts.length && firstMon?.pool != null) hourlyPts.push([x(firstMon.t), y(firstMon.pool)]);
  const monPts = monRows.map((r): Point | null => (r.pool == null ? null : [x(r.t), y(r.pool)]));
  const extPts: Point[] = [];
  for (const r of s.ext) if (r.pool != null) extPts.push([x(r.t), y(r.pool)]);
  const lastMon = [...monPts].reverse().find((p) => p !== null) ?? null;
  const firstExt = extPts[0];
  const connector = lastMon && firstExt ? pathOf([lastMon, firstExt]) : "";

  // gap panel: line and the area between the line and zero, segment by segment
  const gapPts = rows.map((r): Point | null => (r.basis == null ? null : [x(r.t), yg(r.basis)]));
  let gapFill = "";
  let seg: Point[] = [];
  const flush = () => {
    const head = seg[0], last = seg.at(-1);
    if (seg.length > 1 && head && last) gapFill += pathOf(seg) + ` L${last[0].toFixed(1)} ${yg(0).toFixed(1)} L${head[0].toFixed(1)} ${yg(0).toFixed(1)} Z `;
    seg = [];
  };
  for (const p of gapPts) {
    if (p) seg.push(p);
    else flush();
  }
  flush();

  // markers: freeze start, reopen, last point
  for (const w of frozen) {
    if (w.a >= t0) {
      marks.push({ kind: "line", cls: "edge", x1: x(w.a).toFixed(1), y1: mainTop, x2: x(w.a).toFixed(1), y2: gapBot });
      marks.push({ kind: "text", cls: "label", x: (x(w.a) + 6).toFixed(1), y: mainTop + 30, text: `freeze starts ${wdEt(w.a)} 20:00 ET` });
    }
    if (w.b <= t1) {
      const firstAfter = w.observed?.first_update_after?.[sym];
      marks.push({ kind: "line", cls: "edge reopen", x1: x(w.b).toFixed(1), y1: mainTop - 10, x2: x(w.b).toFixed(1), y2: gapBot });
      marks.push({ kind: "path", cls: "flag", d: `M${(x(w.b) - 5).toFixed(1)} ${mainTop - 14} h10 l-5 8 z` });
      const lbl = `feed reopened ${new Date(w.b).toISOString().slice(11, 16)} UTC, ${fmtEt(w.b).slice(-8)}` +
        (firstAfter && W > 700 ? `, first ${sym} round ${fmtClock(Date.parse(firstAfter))}` : "");
      const est = lbl.length * 6.8; // keep the label inside the drawing at any width
      const lx = Math.max(padL, Math.min(x(w.b) + 8, W - padR - est));
      marks.push({ kind: "text", cls: "label neon", x: lx.toFixed(1), y: mainTop - 4, text: lbl });
    }
  }
  const lastRow = [...rows].reverse().find((r) => r.pool != null && r.basis != null);
  let lastMark: LastMark | null = null;
  if (lastRow?.pool != null) {
    const lx = x(lastRow.t), ly = y(lastRow.pool);
    lastMark = {
      cx: lx.toFixed(1), cy: ly.toFixed(1),
      labelX: Math.min(lx + 8, W - padR - 4).toFixed(1), labelY: (ly - 10).toFixed(1),
      anchor: lx + 8 > W - padR - 70 ? "end" : "start",
      text: fmtPct(lastRow.basis),
    };
  }

  return {
    view,
    decor: [...shade, ...grid, ...shadeLabels, ...ylab, ...axis, ...marks],
    layers: { gapFill, gapLine: pathOf(gapPts), oracle: pathOf(opts), hourly: pathOf(hourlyPts), monitor: pathOf(monPts), connector, live: pathOf(extPts) },
    lastMark,
  };
}
